import React, { useState } from "react";
import { Button, Modal, Progress, message } from "antd";
import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import { exportApi } from "../../api/exportApi";
import { handleExportDownload } from "../../utils/exportUtils";

/**
 * 通用筛选导出按钮组件
 * 支持根据筛选条件导出各种资源类型的数据
 *
 * @param {Object} props - 组件属性
 * @param {string} props.resourceType - 资源类型 (如 "uld", "flight_operation", "schedule")
 * @param {Object} props.filterParams - 筛选参数
 * @param {Object} props.options - 导出选项
 * @param {string} props.buttonText - 按钮文字 (默认为"导出数据")
 * @param {Object} props.buttonProps - 传递给Button组件的其他属性
 * @param {Function} props.onExportStart - 导出开始回调
 * @param {Function} props.onExportComplete - 导出完成回调
 * @param {Function} props.onExportError - 导出错误回调
 * @param {Function} props.beforeExport - 导出前钩子，返回 Promise<boolean>；解析为 false 时中止本次导出（用于业务确认，如多分配对象混入提示）
 */
const FilteredExportButton = ({ resourceType, filterParams = {}, options = {}, buttonText = "导出数据", buttonProps = {}, onExportStart = null, onExportComplete = null, onExportError = null, beforeExport = null }) => {
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);

    // 根据资源类型获取对应的API方法
    const getExportApiMethod = (resourceType) => {
        const apiMethods = {
            uld: exportApi.exportFilteredUlds,
            flight_operation: exportApi.exportFlightOperations,
            uld_airport_statistics: exportApi.exportUldAirportStatistics,
            uld_flight_statistics: exportApi.exportUldFlightStatistics,
            uld_inactive: exportApi.exportInactiveUlds,
            mawb_usages: exportApi.exportMawbUsages,
            // 可以继续添加其他资源类型的筛选导出方法
        };

        return apiMethods[resourceType];
    };

    // 处理导出操作
    const handleExport = async () => {
        if (loading) return;

        const exportMethod = getExportApiMethod(resourceType);
        if (!exportMethod) {
            message.error(`不支持的资源类型: ${resourceType}`);
            return;
        }

        try {
            setLoading(true);

            // 导出前业务确认（如多分配对象混入提示）。返回 false 则中止本次导出。
            if (beforeExport) {
                const proceed = await beforeExport();
                if (!proceed) {
                    setLoading(false);
                    return;
                }
            }

            setModalVisible(true);
            setExportProgress(10);
            onExportStart?.();

            // 调用对应的筛选导出API
            const response = await exportMethod(filterParams, options);

            // 检查响应格式
            if (response.data && response.data.success) {
                setExportProgress(100);

                // 延迟短暂时间后下载并关闭模态框
                setTimeout(() => {
                    if (response.data.download_url) {
                        handleExportDownload(response.data.download_url, response.data.file_name);
                    }
                    message.success(response.data.message || "导出成功");
                    setModalVisible(false);
                    setLoading(false);
                    onExportComplete?.(response.data);
                }, 500);
            } else {
                throw new Error(response.data?.message || "导出失败");
            }
        } catch (error) {
            console.error(`导出${resourceType}数据失败:`, error);
            const errorMessage = error.response?.data?.detail || error.message || "导出失败，请稍后重试";
            message.error(errorMessage);
            setModalVisible(false);
            setLoading(false);
            onExportError?.(error);
        }
    };

    // 取消导出
    const handleCancel = () => {
        setModalVisible(false);
        setLoading(false);
        message.info("已取消导出");
    };

    // 处理buttonProps中的onClick事件，确保既执行传入的onClick又执行导出逻辑
    const handleButtonClick = (e) => {
        // 先执行传入的onClick（如阻止事件冒泡）
        if (buttonProps.onClick) {
            buttonProps.onClick(e);
        }
        // 然后执行导出逻辑
        handleExport();
    };

    // 从buttonProps中提取onClick，避免覆盖我们的处理函数
    const { onClick: _, ...restButtonProps } = buttonProps;

    return (
        <>
            <Button {...restButtonProps} loading={loading} onClick={handleButtonClick} icon={loading ? <LoadingOutlined /> : restButtonProps.icon || <DownloadOutlined />}>
                {buttonText}
            </Button>

            <Modal 
                title={`正在导出${
                    resourceType === "uld" ? "ULD" : 
                    resourceType === "uld_airport_statistics" ? "ULD机场维度统计" :
                    resourceType === "uld_flight_statistics" ? "ULD航班维度统计" :
                    resourceType === "uld_inactive" ? "长时间无动态ULD" :
                    resourceType === "mawb_usages" ? "主单号明细" :
                    resourceType
                }数据`} 
                open={modalVisible} 
                onCancel={handleCancel} 
                footer={null} 
                closable={true}
            >
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <Progress percent={exportProgress} status={exportProgress < 100 ? "active" : "success"} />
                    <div style={{ marginTop: 16 }}>{exportProgress < 100 ? "正在生成导出文件，请稍候..." : "导出完成，正在下载..."}</div>
                </div>
            </Modal>
        </>
    );
};

export default FilteredExportButton;
